import { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, ChevronUp, ChevronDown, X, CheckCircle2, Circle, Trophy } from "lucide-react";
import { useOnboardingChecklist, type ChecklistItem } from "@/hooks/useOnboardingChecklist";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

function ProgressColor(percent: number) {
  if (percent > 70) return "bg-green-500";
  if (percent >= 40) return "bg-yellow-500";
  return "bg-primary";
}

export function OnboardingWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);
  const prevFirstDealWon = useRef(false);
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    checklistItems,
    completedCount,
    totalCount,
    progressPercent,
    isActivated,
    isFirstDealWon,
    widgetDismissed,
    widgetMinimized,
    isLoading,
    state,
    toggleMinimize,
    dismissWidget,
  } = useOnboardingChecklist();

  // Sort: pending first, completed last — must be before early returns
  const sortedItems = useMemo(() => {
    const pending = checklistItems.filter((i) => !i.completed);
    const completed = checklistItems.filter((i) => i.completed);
    return [...pending, ...completed];
  }, [checklistItems]);

  const progressBarColor = ProgressColor(progressPercent);

  // Detect first deal won transition
  useEffect(() => {
    if (isFirstDealWon && !prevFirstDealWon.current && !localDismissed) {
      setShowCompletionMessage(true);
      dismissTimerRef.current = setTimeout(() => {
        setLocalDismissed(true);
        setShowCompletionMessage(false);
        dismissWidget();
      }, 4000);
    }
    prevFirstDealWon.current = !!isFirstDealWon;
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstDealWon]);

  const handleCompletionClose = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setLocalDismissed(true);
    setShowCompletionMessage(false);
    dismissWidget();
  };

  // Don't render conditions
  if (isLoading) return null;
  if (!state) return null;
  if (widgetDismissed || localDismissed) return null;
  if (isActivated && isFirstDealWon && !showCompletionMessage) return null;
  if (location.pathname === "/app/onboarding") return null;

  // Completion message state
  if (showCompletionMessage) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="completion"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
          style={{ transformOrigin: "bottom right" }}
          className="fixed bottom-6 right-6 z-50 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl"
        >
          <div className="p-5 text-center space-y-3">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-yellow-100"
            >
              <Trophy className="h-7 w-7 text-yellow-500" />
            </motion.div>
            <h3 className="text-lg font-bold text-foreground">🏆 Missão cumprida!</h3>
            <p className="text-sm text-muted-foreground">
              Você configurou seu sistema e fechou sua primeira venda. O SoluxCRM está pronto para escalar com você.
            </p>
            <div className="pt-1">
              <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 4, ease: "linear" }}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1"
              onClick={handleCompletionClose}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Fechar
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (widgetMinimized) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="minimized"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          style={{ transformOrigin: "bottom right" }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            onClick={() => toggleMinimize(false)}
            className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 shadow-lg transition-shadow hover:shadow-xl"
          >
            <Rocket className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              {completedCount} de {totalCount} concluídos
            </span>
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full transition-all ${progressBarColor}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          </button>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="expanded"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        style={{ transformOrigin: "bottom right" }}
        className="fixed bottom-6 right-6 z-50 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl"
      >
        {/* Header */}
        <div className="border-b border-border p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                Começando com o SoluxCRM
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => toggleMinimize(true)}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              {confirmDismiss ? (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-destructive"
                    onClick={() => {
                      dismissWidget();
                      setConfirmDismiss(false);
                    }}
                  >
                    Sim
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setConfirmDismiss(false)}
                  >
                    Não
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setConfirmDismiss(true)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
          {confirmDismiss && (
            <p className="mb-2 text-xs text-muted-foreground">
              Tem certeza? Você pode reabrir em Configurações.
            </p>
          )}
          <p className="mb-1.5 text-xs text-muted-foreground">
            {completedCount} de {totalCount} tarefas concluídas
          </p>
          <div className="flex items-center gap-2">
            <Progress value={progressPercent} className="h-2 flex-1" />
            <span className="text-xs font-medium text-muted-foreground">
              {progressPercent}%
            </span>
          </div>
        </div>

        {/* Items List */}
        <ScrollArea className="max-h-[300px]">
          <div className="p-2">
            {sortedItems.map((item, index) => (
              <ChecklistRow key={item.key} item={item} index={index} navigate={navigate} />
            ))}
          </div>
        </ScrollArea>

        {/* Footer */}
        <Footer isActivated={isActivated} isFirstDealWon={isFirstDealWon} completedCount={completedCount} />
      </motion.div>
    </AnimatePresence>
  );
}

function ChecklistRow({
  item,
  index,
  navigate,
}: {
  item: ChecklistItem;
  index: number;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => item.route && navigate(item.route)}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted/50"
    >
      {item.completed ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
      ) : (
        <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm ${
            item.completed
              ? "text-muted-foreground line-through opacity-60"
              : "font-medium text-foreground"
          }`}
        >
          {item.label}
          {item.required && !item.completed && (
            <span className="ml-1 text-xs text-primary">*</span>
          )}
        </p>
      </div>
    </motion.button>
  );
}

function Footer({
  isActivated,
  isFirstDealWon,
  completedCount,
}: {
  isActivated: boolean;
  isFirstDealWon: boolean;
  completedCount: number;
}) {
  if (isActivated && isFirstDealWon) return null;

  let message = "";
  if (!isActivated && completedCount >= 3) {
    message = "Você está quase lá! Continue configurando.";
  } else if (isActivated && !isFirstDealWon) {
    message = "✅ Setup completo! Agora faça sua primeira venda.";
  } else {
    return null;
  }

  return (
    <div className="border-t border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
